<template>
  <a-modal
    v-model:visible="visible"
    title="AOD校验工具"
    :width="700"
    :body-style="{
      minHeight: '400px',
      maxHeight: '700px',
      overflow: 'auto'
    }"
    @ok="handleClose"
  >
    <!-- 图片上传区域 -->
    <div
      class="w-full h-[300px] border border-dashed border-gray-500 rounded-lg flex items-center justify-center mb-4 overflow-hidden relative"
    >
      <img
        v-if="imageUrl"
        :src="imageUrl"
        class="max-w-full max-h-full object-contain"
      />
      <a-button v-else @click="pickImage">上传图片</a-button>
      <div
        v-if="imageUrl"
        class="absolute top-2 right-2 cursor-pointer bg-black/50 rounded-full w-6 h-6 flex items-center justify-center text-white text-xs"
        @click="clearImage"
      >
        ✕
      </div>
    </div>

    <!-- 检验规则 -->
    <div class="mb-4">
      <div class="font-bold mb-2">检验规则</div>
      <div class="flex flex-col gap-1 text-sm opacity-80">
        <div v-for="(item, index) in ruleDescArr" :key="index" class="flex items-center flex-wrap">
          <template v-if="item.tooltip && item.desc.includes(': ')">
            <span>{{ index + 1 }}. {{ item.desc.split(': ')[0] }}</span>
            <a-tooltip :title="item.tooltip">
              <icon class="ml-0.5 mr-0.5 !cursor-default" icon="help.svg" :width="14" :height="14" fill="none" />
            </a-tooltip>
            <span>: {{ item.desc.split(': ').slice(1).join(': ') }}</span>
          </template>
          <template v-else>
            {{ index + 1 }}. {{ item.desc }}
          </template>
        </div>
      </div>
    </div>

    <!-- 校验结果 -->
    <div v-if="validResult">
      <div class="font-bold mb-2">校验结果</div>
      <div class="flex flex-col gap-1 text-sm opacity-80">
        <div v-if="validResult.nonBlackRatio !== undefined">
          1. 非黑像素占比：{{ (validResult.nonBlackRatio * 100).toFixed(2) }}%
        </div>
        <div v-if="validResult.rgbInvalidRatio !== undefined">
          2. 不合规RGB像素占比：{{
            (validResult.rgbInvalidRatio * 100).toFixed(2)
          }}%
        </div>
        <div v-if="validResult.illegalColorRatio !== undefined">
          3. 不合法颜色（max(R,G)/2>=B）像素占比：{{
            (validResult.illegalColorRatio * 100).toFixed(2)
          }}%
        </div>
      </div>
      <div class="mt-3 text-xs opacity-50">
        注：此处并未区分可移动元素、部分移动元素、不可移动元素，统一按部分移动元素校验，和真实表盘结果有出入，仅供参考。
      </div>
    </div>

    <template #footer>
      <a-button @click="handleClose">关闭</a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useWatchfaceAodPixel } from '@/hooks';
import { pickFile } from '@/utils/fs/pick-file.utils';
import { getImageBase64 } from '@/utils/fs/image.utils';
import { round } from 'lodash-es';

const { getRule, ruleDescArr } = useWatchfaceAodPixel();

const visible = ref(false);
const imageUrl = ref('');
const validResult = ref<{
  nonBlackRatio?: number;
  rgbInvalidRatio?: number;
  illegalColorRatio?: number;
} | null>(null);

const pickImage = async () => {
  const files = await pickFile({
    extensions: ['png', 'jpg', 'jpeg', 'bmp']
  });
  if (!files?.length) return;
  const filePath = files[0];
  const base64 = await getImageBase64(filePath);
  if (!base64) return;
  imageUrl.value = base64;
  await validateImage();
};

const validateImage = async () => {
  if (!imageUrl.value) return;
  const rule = getRule();
  if (!rule) return;

  const img = new Image();
  img.src = imageUrl.value;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.drawImage(img, 0, 0);

  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  let allPixels = 0;
  let notBlackPixels = 0;
  let rgbInvalidPixels = 0;
  let illegalColorPixels = 0;
  const rgbValidFn = rule.RGB?.partiallyMovable?.valid;
  const checkIllegalColor = !!rule['max(R,G)/2>=B'];

  for (let i = 0; i < data.length; i += 4) {
    allPixels++;
    const R = data[i];
    const G = data[i + 1];
    const B = data[i + 2];
    const A = data[i + 3];
    const isBlack = (R === 0 && G === 0 && B === 0) || A === 0;
    if (!isBlack) {
      notBlackPixels++;
      if (rgbValidFn && !rgbValidFn(R, G, B)) {
        rgbInvalidPixels++;
      }
      if (checkIllegalColor && !(Math.max(R, G) / 2 >= B)) {
        illegalColorPixels++;
      }
    }
  }

  validResult.value = {
    nonBlackRatio:
      rule.nonBlackRatio && allPixels > 0
        ? round(notBlackPixels / allPixels, 4)
        : undefined,
    rgbInvalidRatio:
      rgbValidFn && notBlackPixels > 0
        ? round(rgbInvalidPixels / notBlackPixels, 4)
        : undefined,
    illegalColorRatio:
      checkIllegalColor && allPixels > 0
        ? round(illegalColorPixels / allPixels, 4)
        : undefined
  };
};

const clearImage = () => {
  imageUrl.value = '';
  validResult.value = null;
};

const handleClose = () => {
  visible.value = false;
  clearImage();
};

defineExpose({
  open: () => {
    clearImage();
    visible.value = true;
  }
});
</script>
